<script setup>
  import DropdownWidget from "./dropdown-widget.vue";

  import {
    breakdownRecipePath,
    copyRecipePath,
    costRecipePath,
    createIngredientFromRecipePath,
    editRecipePath,
    historyRecipePath,
    ingredientMetasRecipePath,
    ingredientAllergensRecipePath,
    recipeCardRecipePath,
    recipePath,
    recipeLockPath,
    scaleRecipePath,
    toggleArchiveRecipePath,
    ingredientPath,
    exportBusinessPlanRecipePath,
  } from "routes";

  defineProps({
    recipe: { type: Object, required: true },
    enableArchiving: { type: Boolean, default: true },
    allowLocking: { type: Boolean, default: false },
    deleteConfirms: { type: Object, required: true },
  });
</script>

<template>
  <DropdownWidget v-if="!recipe.empty || enableArchiving || (allowLocking && recipe.locked)">
    <template #trigger>
      <button class="button is-outlined is-info is-small">
        <span>Actions</span>
        <span class="icon is-small">
          <i class="fas fa-angle-down"></i>
        </span>
      </button>
    </template>

    <template #content>
      <div class="is-hidden-tablet">
        <a class="dropdown-item" :href="editRecipePath(recipe.id)">
          <span class="icon-text">
            <span class="icon">
              <i class="fa fa-edit"></i>
            </span>
            <span>Recipe</span>
          </span>
        </a>

        <hr v-if="recipe.empty" class="dropdown-divider" />
      </div>

      <div v-if="!recipe.empty" class="is-hidden-tablet">
        <a class="dropdown-item" :href="recipePath(recipe.id)">
          <span class="icon-text">
            <span class="icon">
              <i class="fa fa-eye"></i>
            </span>
            <span>Label</span>
          </span>
        </a>

        <a class="dropdown-item" :href="ingredientMetasRecipePath(recipe.id)">
          <span class="icon-text is-flex-wrap-nowrap">
            <span class="icon">
              <i class="fa-duotone fa-list"></i>
            </span>
            <span>Ingredient Statement</span>
          </span>
        </a>

        <a class="dropdown-item" :href="ingredientAllergensRecipePath(recipe.id)">
          <span class="icon-text is-flex-wrap-nowrap">
            <span class="icon">
              <i class="fa-regular fa-peanuts"></i>
            </span>
            <span>Allergens</span>
          </span>
        </a>

        <a class="dropdown-item" :href="costRecipePath(recipe.id)">
          <span class="icon-text">
            <span class="icon">
              <i class="fa fa-dollar-sign"></i>
            </span>
            <span>Cost</span>
          </span>
        </a>

        <a class="dropdown-item" :href="breakdownRecipePath(recipe.id)">
          <span class="icon-text is-flex-wrap-nowrap">
            <span class="icon">
              <i class="fa-duotone fa-grid-2"></i>
            </span>
            <span>Nutrition Breakdown</span>
          </span>
        </a>

        <a class="dropdown-item" :href="recipeCardRecipePath(recipe.id)">
          <span class="icon-text">
            <span class="icon">
              <i class="far fa-rectangle-list"></i>
            </span>
            <span>Card</span>
          </span>
        </a>

        <a class="dropdown-item" :href="historyRecipePath(recipe.id)">
          <span class="icon-text">
            <span class="icon">
              <i class="fa fa-clock-rotate-left"></i>
            </span>
            <span>History</span>
          </span>
        </a>

        <hr class="dropdown-divider" />
      </div>

      <div v-if="!recipe.empty">
        <a class="dropdown-item" data-method="post" :href="copyRecipePath(recipe.id)">
          <span class="icon-text">
            <span class="icon">
              <i class="fa fa-copy"></i>
            </span>
            <span>Copy</span>
          </span>
        </a>

        <a class="dropdown-item" data-remote="true" :href="scaleRecipePath(recipe.id)">
          <span class="icon-text">
            <span class="icon">
              <i class="fa fa-up-right-and-down-left-from-center"></i>
            </span>
            <span>Scale...</span>
          </span>
        </a>

        <a
          v-if="!recipe.subrecipe"
          class="dropdown-item"
          data-method="post"
          :href="createIngredientFromRecipePath(recipe.id)"
        >
          <span class="icon-text is-flex-wrap-nowrap">
            <span class="icon">
              <i class="fa-duotone fa-filters"></i>
            </span>
            <span>Create Subrecipe</span>
          </span>
        </a>

        <a
          v-if="recipe.subrecipe"
          class="dropdown-item"
          data-confirm="This will delete the subrecipe ingredient associated with this recipe. Are you sure you would like to continue?"
          data-method="delete"
          :href="ingredientPath(recipe.subrecipe)"
        >
          <span class="icon-text is-flex-wrap-nowrap">
            <span class="icon has-text-danger">
              <i class="fa fa-trash-alt"></i>
            </span>
            <span>Undo subrecipe</span>
          </span>
        </a>

        <a
          class="dropdown-item"
          :href="exportBusinessPlanRecipePath(recipe.id, { format: 'csv' })"
          title="Download Spreadsheet / Business Plan"
        >
          <span class="icon-text">
            <span class="icon">
              <i class="fa fa-download"></i>
            </span>
            <span>Download CSV</span>
          </span>
        </a>

        <hr v-if="enableArchiving || allowLocking" class="dropdown-divider" />
      </div>

      <a
        v-if="allowLocking && (!recipe.empty || recipe.locked)"
        class="dropdown-item"
        data-remote="true"
        :href="recipeLockPath(recipe.id)"
      >
        <span class="icon-text">
          <span class="icon">
            <i class="fa" :class="recipe.locked ? 'fa-unlock' : 'fa-lock'"></i>
          </span>
          <span>{{ recipe.locked ? "Unlock recipe" : "Lock recipe" }}</span>
        </span>
      </a>

      <template v-if="enableArchiving">
        <a
          v-if="!recipe.archived"
          class="dropdown-item"
          data-method="put"
          :href="toggleArchiveRecipePath(recipe.id)"
        >
          <span class="icon-text">
            <span class="icon">
              <i class="far fa-eye-slash"></i>
            </span>
            <span>Archive</span>
          </span>
        </a>

        <a
          v-if="recipe.archived"
          class="dropdown-item"
          data-method="put"
          :href="toggleArchiveRecipePath(recipe.id)"
        >
          <span class="icon-text">
            <span class="icon has-text-success">
              <i class="far fa-trash-undo"></i>
            </span>
            <span>Restore</span>
          </span>
        </a>

        <a
          v-if="recipe.archived && !recipe.locked"
          class="dropdown-item"
          :data-confirm="recipe.expired ? deleteConfirms.expired : deleteConfirms.editable"
          data-method="delete"
          :href="recipePath(recipe.id)"
        >
          <span class="icon-text">
            <span class="icon has-text-danger">
              <i class="fa fa-trash-alt"></i>
            </span>
            <span>Delete</span>
          </span>
        </a>
      </template>
    </template>
  </DropdownWidget>
</template>
